import { useState, useCallback } from 'react';
import { Link, useNavigate } from "@/lib/router-compat";
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Menu } from 'lucide-react';
import Logo from '@/components/Logo';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' as const } },
};

export const HomeNav = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleMobileNav = useCallback((path: string) => {
    setMobileMenuOpen(false);
    setTimeout(() => navigate(path), 50);
  }, [navigate]);

  const links = [
    { to: '/how-it-works', label: t('nav.how_it_works') },
    { to: '/events', label: t('nav.experience') },
    { to: '/locations', label: t('nav.cities') },
    { to: '/pricing', label: t('nav.pricing') },
  ];

  return (
    <div className="fixed top-0 left-0 right-0 z-50 flex justify-center px-3 pt-3 md:pt-4">
      {/* Pure-CSS entrance so the nav never depends on the JS bundle booting */}
      <nav
        className="am-fade w-full max-w-6xl rounded-full bg-secondary/95 border border-border/30 shadow-elevated px-3 md:px-5"
      >
        <div className="flex items-center justify-between h-12 md:h-[52px]">
          <div className="shrink-0">
            <Link to="/"><Logo size="lg" /></Link>
          </div>

          {/* Desktop Navigation */}
          <div
            className="am-fade hidden lg:flex items-center gap-6 2xl:gap-8"
            style={{ animationDelay: '0.2s' }}
          >
            {links.map((link) => (
              <div key={link.to}>
                <Link to={link.to} className="text-[13px] font-semibold tracking-wide text-primary/70 hover:text-primary transition-colors uppercase">
                  {link.label}
                </Link>
              </div>
            ))}
          </div>

          {/* Desktop Action buttons */}
          <div
            className="am-fade hidden lg:flex items-center gap-2 shrink-0"
            style={{ animationDelay: '0.5s' }}
          >
            <Link to="/auth">
              <Button variant="ghost" className="rounded-full px-4 h-8 text-[12px] font-semibold text-primary/80 hover:text-primary hover:bg-primary/10 transition-all">
                {t('nav.sign_in')}
              </Button>
            </Link>
            <Link to="/quiz">
              <Button className="rounded-full px-5 h-8 text-[12px] font-semibold bg-primary text-primary-foreground hover:bg-primary/90 shadow-xs shadow-primary/20 transition-all">
                {t('nav.find_my_experience')}
              </Button>
            </Link>
          </div>

          {/* Mobile Navigation */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <div className="am-fade" style={{ animationDelay: '0.3s' }}>
                <Button variant="ghost" size="icon" aria-label="Open menu" className="text-primary/80 hover:text-primary hover:bg-primary/10">
                  <Menu className="h-5 w-5" />
                </Button>
              </div>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] pt-12">
              <motion.nav
                className="flex flex-col gap-5"
                initial="hidden"
                animate="visible"
                variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.1 } } }}
              >
                {links.map((link) => (
                  <motion.div key={link.to} variants={fadeInUp}>
                    <button type="button" className="text-base font-semibold text-foreground/70 hover:text-foreground transition-colors text-left" onClick={() => handleMobileNav(link.to)}>{link.label}</button>
                  </motion.div>
                ))}
                <motion.div variants={fadeInUp} className="mt-3">
                  <Button variant="outline" className="w-full rounded-full" onClick={() => handleMobileNav('/auth')}>{t('nav.sign_in')}</Button>
                </motion.div>
                <motion.div variants={fadeInUp}>
                  <Button className="w-full rounded-full" onClick={() => handleMobileNav('/quiz')}>{t('nav.find_my_experience')}</Button>
                </motion.div>
              </motion.nav>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </div>
  );
};
